import { useCallback, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import useFetchEligibleVppPrograms from "~/api/hooks/useFetchEligibleVppPrograms"
import useFetchProposalVppIncentives from "~/api/hooks/useFetchProposalVppIncentives"
import { proposalVppIncentiveService } from "~/api/services/proposal-vpp-incentive.service"
import { SpinnerCircle } from "~/components/Spinner"
import { Switch } from "~/components/ui-components/switch"
import type { IPart } from "~/containers/Proposal/Setup/Form/setup-steps-types"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"

import { SetupMenuFormFooter } from "../SetupMenuFooter"

const currencyFormatter = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
  minimumFractionDigits: 2,
})

const VppIncentivesSelection: IPart[`form`] = () => {
  const proposal = useProposalContext()
  const proposalId = proposal.local.id
  const queryClient = useQueryClient()

  const { data: eligiblePrograms, isLoading: isLoadingEligible } =
    useFetchEligibleVppPrograms(proposalId)
  const { data: associations, isLoading: isLoadingAssociations } =
    useFetchProposalVppIncentives(proposalId)

  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map())
  const [isSaving, setIsSaving] = useState(false)

  const initialSelectedIds = useMemo(
    () => new Set((associations ?? []).map((a) => a.vppProgramId)),
    [associations],
  )

  const isSelected = useCallback(
    (programId: string): boolean => {
      const override = overrides.get(programId)
      if (override !== undefined) return override
      return initialSelectedIds.has(programId)
    },
    [overrides, initialSelectedIds],
  )

  const toggleProgram = useCallback(
    (programId: string) => {
      setOverrides((prev) => {
        const next = new Map(prev)
        const current = next.get(programId)
        if (current !== undefined) {
          next.delete(programId)
        } else {
          next.set(programId, !initialSelectedIds.has(programId))
        }
        return next
      })
    },
    [initialSelectedIds],
  )

  const isDirty = overrides.size > 0
  const isLoading = isLoadingEligible || isLoadingAssociations

  const onSubmit = useCallback(async () => {
    const toAdd: string[] = []
    const toRemove: string[] = []

    overrides.forEach((selected, programId) => {
      const wasSelected = initialSelectedIds.has(programId)
      if (selected && !wasSelected) toAdd.push(programId)
      if (!selected && wasSelected) toRemove.push(programId)
    })

    setIsSaving(true)
    try {
      const promises: Promise<unknown>[] = []
      if (toAdd.length > 0) {
        promises.push(
          proposalVppIncentiveService.associatePrograms(proposalId!, {
            vppProgramIds: toAdd,
          }),
        )
      }
      for (const programId of toRemove) {
        promises.push(
          proposalVppIncentiveService.deleteAssociation(proposalId!, programId),
        )
      }
      await Promise.all(promises)
      setOverrides(new Map())
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.proposalVppIncentives.byProposal(proposalId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.proposalVppIncentives.eligible(proposalId),
        }),
      ])
    } finally {
      setIsSaving(false)
    }
  }, [overrides, initialSelectedIds, proposalId, queryClient])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerCircle size={32} />
      </div>
    )
  }

  return (
    <form id="vpp-incentives-selection-form">
      {!eligiblePrograms?.length ? (
        <p className="py-8 text-center text-sm text-gray-500">
          No eligible VPP programs for this proposal.
        </p>
      ) : (
        <section className="space-y-3 px-1 py-2">
          {eligiblePrograms.map((program) => (
            <article
              key={program.id}
              className="rounded-lg border border-border bg-white px-4 py-3 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-foreground">
                  {program.name}
                  {program.calculatedValue > 0 && (
                    <span className="ml-2 text-xs font-normal text-muted">
                      {currencyFormatter.format(program.calculatedValue)}
                    </span>
                  )}
                </h4>
                <Switch
                  color="blue"
                  checked={isSelected(program.id)}
                  onChange={() => toggleProgram(program.id)}
                />
              </div>
              {program.description && (
                <p className="mt-1 text-xs text-muted">{program.description}</p>
              )}
            </article>
          ))}
        </section>
      )}
      <SetupMenuFormFooter
        onSubmit={onSubmit}
        isLoading={isSaving}
        isDirty={isDirty}
      />
    </form>
  )
}

export default VppIncentivesSelection
